import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Trash2 } from 'lucide-react'
import { cx } from '@/lib/clases'
import { useAtajo } from '@/lib/atajos'
import { formatearFecha } from '@/lib/fecha'
import { formatearRNC, esRNCValido, limpiarRNC } from '@/lib/rnc'
import { Boton, BotonIcono } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { BuscadorEntidad, type OpcionEntidad } from '@/ui/BuscadorEntidad'
import { CampoFormulario, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { EntradaMonetaria } from '@/ui/CampoMonetario'
import { InsigniaEstado, PuntoEstado } from '@/ui/InsigniaEstado'
import { ENCF, Monto } from '@/ui/Numero'
import { useAvisos } from '@/ui/Avisos'
import { INDICADORES, type IndicadorFacturacion } from '@/dominio/documentos/calculo'
import { traducirErrorApi, mensajeIdempotenciaRepetida } from '@/dominio/documentos/errores'
import { TIPOS_ECF, type TipoECF } from '@/dominio/documentos/tipos'
import { useEmitirDocumento, useProximoENCF } from '@/dominio/documentos/useDocumentos'
import { useFormularioEmision, type LineaFormulario } from '@/dominio/documentos/useFormularioEmision'
import { useBuscarContrapartes } from '@/dominio/contrapartes/useContrapartes'
import { indicadorDeTasa } from '@/dominio/productos/tipos'
import { useBuscarProductos } from '@/dominio/productos/useProductos'
import { EncabezadoPantalla } from './-pantalla'

/**
 * Emisión (E34). La pantalla que más importa.
 *
 * Las cuatro decisiones que la hacen correcta, todas del documento:
 *
 * 1. **La clave de idempotencia se genera una vez por instancia de formulario.**
 *    Vive en `useFormularioEmision`, que la toma de `useClaveIdempotencia`
 *    (un `useRef`, no un `useMemo`). Esta pantalla **no la construye ni la
 *    toca**: solo se la pasa al hook de dominio.
 * 2. **El dígito verificador del RNC avisa, no bloquea** (regla 8). La
 *    estructura sí bloquea; el dígito aparece como aviso ámbar aparte.
 * 3. **Los totales se calculan localmente para respuesta instantánea, pero el
 *    servidor recalcula y su resultado manda.**
 * 4. **Operable completa con teclado.** El foco entra en el cliente al abrir,
 *    Enter en la última línea crea la siguiente, `Ctrl+Enter` emite.
 */
export const Route = createFileRoute('/emitir')({
  component: PantallaEmitir,
})

/** Tipos que el portal ofrece en el selector, en el orden en que se usan. */
const TIPOS_OFRECIDOS: readonly TipoECF[] = ['31', '32', '33', '34', '41', '43', '44', '45', '46', '47']

const MONEDAS: readonly { valor: 'DOP' | 'USD' | 'EUR'; etiqueta: string }[] = [
  { valor: 'DOP', etiqueta: 'DOP · peso dominicano' },
  { valor: 'USD', etiqueta: 'USD · dólar' },
  { valor: 'EUR', etiqueta: 'EUR · euro' },
]

function PantallaEmitir() {
  const navegar = useNavigate()
  const { publicar } = useAvisos()
  const formulario = useFormularioEmision()
  const emision = useEmitirDocumento(formulario.claveIdempotencia)
  const proximo = useProximoENCF(formulario.tipo)

  const [consultaCliente, setConsultaCliente] = useState('')
  const zonaCliente = useRef<HTMLDivElement>(null)
  const filasRef = useRef(new Map<string, HTMLInputElement>())

  const clientes = useBuscarContrapartes(consultaCliente)
  // Aquí había una segunda consulta al «directorio de DGII», contra
  // `GET /interna/contrapartes/{rnc}`. Esa ruta no existe: el grupo solo tiene
  // `/{id:guid}`, así que un RNC de nueve dígitos contestaba 404 siempre. El
  // efecto no era un error visible sino una promesa muerta —la opción «desde el
  // directorio de DGII» del desplegable y el autocompletado del nombre no se
  // pintaban jamás— y quedó reportada: el portal no puede consultar el
  // directorio mientras el extremo no exista.

  /**
   * El cursor entra en el campo de cliente al abrir la pantalla.
   *
   * Se busca el control dentro de su zona en vez de pasarle una `ref` al
   * buscador: `BuscadorEntidad` es del sistema de diseño y no expone la del
   * `input`. Cambiarlo para esto significaría tocar `ui/`, que está cerrado y
   * probado, por un detalle de una sola pantalla.
   */
  useEffect(() => {
    zonaCliente.current?.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus()
  }, [])

  const puedeEmitir = formulario.problemas.length === 0 && !emision.isPending

  const emitir = useCallback(() => {
    if (!puedeEmitir) return

    emision.mutate(formulario.aBorrador(), {
      onSuccess: (respuesta) => {
        if (respuesta.repetido === true) {
          // El segundo clic no produce un error: el servidor devolvió el mismo
          // documento y la pantalla aterriza en él. Un mensaje rojo asusta sin
          // motivo, así que se informa en seco.
          const mensaje = mensajeIdempotenciaRepetida()
          publicar({ tono: 'azul', titulo: mensaje.titulo, detalle: mensaje.contexto })
        }
        void navegar({ to: '/documentos', search: { documento: respuesta.id } })
      },
    })
  }, [puedeEmitir, emision, formulario, navegar, publicar])

  // `dentroDeCampos` porque se emite desde dentro de un campo de la última
  // línea, que es donde el usuario tiene el cursor al terminar de capturar.
  useAtajo({ tecla: 'Enter', ctrl: true, dentroDeCampos: true }, emitir)

  const opcionesCliente = useMemo<OpcionEntidad[]>(
    () =>
      (clientes.data?.filas ?? []).map((contraparte) => ({
        id: contraparte.id,
        titulo: contraparte.nombre,
        // El servidor ya manda el número formateado; se prefiere al formateo
        // local para que la ficha y el desplegable no discrepen en un guion.
        detalle: contraparte.rncFormateado ?? formatearRNC(contraparte.rnc),
        marca: contraparte.receptorElectronico ? (
          <InsigniaEstado tono="verde">electrónico</InsigniaEstado>
        ) : (
          <InsigniaEstado tono="neutral">no electrónico</InsigniaEstado>
        ),
      })),
    [clientes.data],
  )

  const elegirCliente = useCallback(
    (opcion: OpcionEntidad) => {
      const enCartera = (clientes.data?.filas ?? []).find((c) => c.id === opcion.id)
      formulario.elegirContraparte({
        id: enCartera?.id,
        rnc: enCartera?.rnc ?? limpiarRNC(String(opcion.detalle ?? '')),
        nombre: opcion.titulo,
        receptorElectronico: enCartera?.receptorElectronico ?? null,
      })
      setConsultaCliente(opcion.titulo)
    },
    [clientes.data, formulario],
  )

  const errorTraducido = emision.isError ? traducirErrorApi(emision.error) : null

  return (
    <div className="flex max-w-[1180px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Nueva factura"
        acciones={
          <div className="flex items-baseline gap-2">
            {/* Es una reserva visual, no una asignación: por eso dice "próximo". */}
            {proximo.data?.hay === false ? (
              // Sin numeración disponible el servidor contesta 200 con el
              // motivo, no 404. Decirlo es la diferencia entre «el sistema está
              // pensando» y «pídele otro rango a DGII antes de seguir».
              <span className="text-etiqueta text-rojo" title={proximo.data.motivo ?? undefined}>
                sin numeración disponible
              </span>
            ) : (
              <>
                <span className="text-etiqueta text-gris-600">próximo</span>
                <ENCF valor={proximo.data?.eNCF ?? null} copiable={false} className="text-gris-900" />
              </>
            )}
          </div>
        }
      />

      {proximo.data?.hay === false ? (
        <Banner tono="rojo" titulo="No queda numeración autorizada para este tipo de comprobante.">
          {proximo.data.motivo ??
            'Solicita un rango nuevo a DGII por la Oficina Virtual antes de seguir facturando este tipo.'}
        </Banner>
      ) : null}

      {errorTraducido !== null ? (
        // Banner arriba del formulario, nunca un modal: los modales para
        // errores están prohibidos porque borran el contexto.
        <Banner
          tono={errorTraducido.tono}
          titulo={errorTraducido.titulo}
          detalleTecnico={errorTraducido.detalleTecnico}
        >
          {errorTraducido.contexto}
        </Banner>
      ) : null}

      {/* Cabecera del documento ---------------------------------------- */}
      <section className="grid grid-cols-1 gap-4 rounded-md border border-gris-300 bg-white p-4 lg:grid-cols-[1fr_220px_220px]">
        <div ref={zonaCliente} className="flex flex-col gap-1">
          <BuscadorEntidad
            etiqueta="Cliente"
            obligatorio
            consulta={consultaCliente}
            onConsultaChange={setConsultaCliente}
            opciones={opcionesCliente}
            cargando={clientes.isFetching}
            onSeleccionar={elegirCliente}
            marcador="Nombre o RNC"
            /*
              Crear desde el mismo campo si no existe: obligar a salir a otra
              pantalla es lo que hace que un ferretero vuelva al talonario.

              Pero **solo cuando lo tecleado es un RNC**. Ofrecerlo sobre un
              nombre suelto crea una contraparte sin número, y el formulario se
              queda bloqueado pidiendo un RNC que el usuario creía haber dado.
              Es mejor no ofrecer la salida que ofrecer una que no lleva a
              ninguna parte.
            */
            onCrear={
              esRNCValido(limpiarRNC(consultaCliente))
                ? (texto) => {
                    const digitos = limpiarRNC(texto)
                    formulario.elegirContraparte({
                      rnc: digitos,
                      // Sin directorio no hay nombre que traer, así que se usa
                      // el propio número como rótulo provisional y se completa
                      // en la ficha. Inventar aquí una razón social sería
                      // grabarla en el comprobante.
                      nombre: formatearRNC(digitos),
                      receptorElectronico: null,
                    })
                  }
                : undefined
            }
            etiquetaCrear="Facturar al RNC"
          />

          {formulario.contraparte !== null ? (
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <span className="mono text-gris-700">{formatearRNC(formulario.contraparte.rnc)}</span>
              {formulario.contraparte.receptorElectronico === true ? (
                <InsigniaEstado tono="verde">receptor electrónico</InsigniaEstado>
              ) : formulario.contraparte.receptorElectronico === false ? (
                <InsigniaEstado tono="neutral" titulo="Entrega la representación impresa.">
                  no es receptor electrónico
                </InsigniaEstado>
              ) : null}
            </div>
          ) : null}

          {/*
            El dígito verificador **avisa, no bloquea**. Hay contribuyentes
            activos cuyo número no pasa el algoritmo, y quien manda es el
            directorio de DGII. Rechazarlo aquí manda al usuario a soporte con
            un número que funciona.
          */}
          {formulario.estadoRNC.avisoDigitoVerificador ? (
            <p className="flex items-start gap-1 pt-1 text-etiqueta text-ambar">
              <PuntoEstado tono="ambar" className="mt-[6px]" />
              <span>
                El dígito verificador de este RNC no cuadra. Puede estar bien igual: revísalo si lo
                tecleaste a mano. No impide emitir.
              </span>
            </p>
          ) : null}
        </div>

        <CampoFormulario etiqueta="Tipo de comprobante" obligatorio>
          {(atributos) => (
            <Selector
              {...atributos}
              value={formulario.tipo}
              onChange={(evento) => formulario.cambiarTipo(evento.target.value as TipoECF)}
            >
              {TIPOS_OFRECIDOS.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {tipo} · {TIPOS_ECF[tipo]}
                </option>
              ))}
            </Selector>
          )}
        </CampoFormulario>

        {/* Moneda: si el tipo es 46 o 47 aparece sola. Sin esto no se emiten. */}
        {formulario.requiereDivisa || formulario.moneda !== 'DOP' ? (
          <div className="flex flex-col gap-4">
            <CampoFormulario
              etiqueta="Moneda"
              ayuda={formulario.requiereDivisa ? 'Este tipo es en divisa.' : undefined}
            >
              {(atributos) => (
                <Selector
                  {...atributos}
                  value={formulario.moneda}
                  onChange={(evento) =>
                    formulario.cambiarMoneda(evento.target.value as 'DOP' | 'USD' | 'EUR')
                  }
                >
                  {MONEDAS.map((moneda) => (
                    <option key={moneda.valor} value={moneda.valor}>
                      {moneda.etiqueta}
                    </option>
                  ))}
                </Selector>
              )}
            </CampoFormulario>

            {formulario.moneda !== 'DOP' ? (
              <CampoFormulario
                etiqueta="Tasa de cambio"
                obligatorio
                // Se congela al emitir y viaja en el documento: recalcularla al
                // reimprimir produce un PDF que no cuadra con el XML firmado.
                ayuda="Se congela al emitir y viaja en el comprobante."
              >
                {(atributos) => (
                  <EntradaTexto
                    {...atributos}
                    numerico
                    inputMode="decimal"
                    value={formulario.tasaCambio}
                    onChange={(evento) => formulario.cambiarTasaCambio(evento.target.value)}
                  />
                )}
              </CampoFormulario>
            ) : null}
          </div>
        ) : (
          <div className="flex items-end">
            <Boton
              variante="discreto"
              tamano="menor"
              onClick={() => formulario.cambiarMoneda('USD')}
            >
              Facturar en otra moneda
            </Boton>
          </div>
        )}

        {/* Condición de pago: el XSD la exige en siete tipos, y a crédito abre la cuenta por cobrar. */}
        {formulario.llevaTipoPago ? (
          <div className="flex flex-col gap-4 sm:flex-row lg:col-span-3">
            <div className="sm:w-[220px]">
              <CampoFormulario etiqueta="Condición de pago" obligatorio>
                {(atributos) => (
                  <Selector
                    {...atributos}
                    value={formulario.tipoPago}
                    onChange={(evento) => formulario.cambiarTipoPago(evento.target.value as '1' | '2' | '3')}
                  >
                    <option value="1">Contado</option>
                    <option value="2">Crédito</option>
                    <option value="3">Gratuito</option>
                  </Selector>
                )}
              </CampoFormulario>
            </div>

            {formulario.tipoPago === '2' ? (
              <div className="sm:w-[220px]">
                <CampoFormulario
                  etiqueta="Plazo en días"
                  obligatorio
                  ayuda={formulario.fechaLimitePago ? `Vence el ${formatearFecha(formulario.fechaLimitePago)}.` : 'De 1 a 365 días.'}
                >
                  {(atributos) => (
                    <EntradaTexto
                      {...atributos}
                      numerico
                      inputMode="numeric"
                      value={formulario.plazoDias}
                      onChange={(evento) => formulario.cambiarPlazoDias(evento.target.value)}
                    />
                  )}
                </CampoFormulario>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* Líneas --------------------------------------------------------- */}
      <TablaDeLineas formulario={formulario} filasRef={filasRef} />

      {/* Totales -------------------------------------------------------- */}
      <div className="flex flex-col items-end gap-3">
        <dl className="w-full max-w-[420px] rounded-md border border-gris-300 bg-white p-4">
          <FilaTotal etiqueta="Subtotal gravado" valor={formulario.totales.subtotalGravado.aTexto()} />
          {!formulario.totales.exento.esCero() ? (
            <FilaTotal etiqueta="Exento" valor={formulario.totales.exento.aTexto()} />
          ) : null}
          {!formulario.totales.itbis18.esCero() ? (
            <FilaTotal etiqueta="ITBIS 18%" valor={formulario.totales.itbis18.aTexto()} />
          ) : null}
          {!formulario.totales.itbis16.esCero() ? (
            <FilaTotal etiqueta="ITBIS 16%" valor={formulario.totales.itbis16.aTexto()} />
          ) : null}
          {!formulario.totales.propinaLegal.esCero() ? (
            <FilaTotal etiqueta="Propina legal 10%" valor={formulario.totales.propinaLegal.aTexto()} />
          ) : null}

          <div className="flex items-baseline justify-between gap-4 border-t border-gris-300 pt-2">
            <dt className="text-seccion text-gris-900">Total</dt>
            <dd>
              <Monto
                valor={formulario.totales.total.aTexto()}
                mostrarMoneda
                className="text-[17px] text-gris-900"
              />
            </dd>
          </div>

          {formulario.totales.totalEnDivisa !== null ? (
            <div className="flex items-baseline justify-between gap-4 pt-1">
              <dt className="text-etiqueta text-gris-600">En {formulario.moneda}</dt>
              <dd>
                <Monto
                  valor={formulario.totales.totalEnDivisa.aTexto()}
                  moneda={formulario.moneda}
                  mostrarMoneda
                  className="text-gris-700"
                />
              </dd>
            </div>
          ) : null}
        </dl>

        {/*
          El usuario no tiene que saber la regla del umbral, pero ve que el
          sistema la conoce. La bifurcación la decide el motor, no la pantalla.
        */}
        {formulario.seEnviaCompleta ? (
          <p className="text-etiqueta text-gris-600">Se enviará como factura completa.</p>
        ) : null}

        <label className="flex items-center gap-2 text-cuerpo text-gris-700">
          <input
            type="checkbox"
            checked={formulario.conPropinaLegal}
            onChange={(evento) => formulario.cambiarPropinaLegal(evento.target.checked)}
            className="size-4 accent-[var(--color-marca)]"
          />
          Cobrar propina legal del 10%
        </label>

        {formulario.problemas.length > 0 ? (
          <ul className="flex flex-col items-end gap-px">
            {formulario.problemas.map((problema) => (
              <li key={problema} className="text-etiqueta text-gris-600">
                {problema}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex items-center gap-2">
          <Boton variante="secundario" disabled>
            Guardar borrador
          </Boton>
          <Boton variante="primario" atajo="Ctrl+⏎" onClick={emitir} disabled={!puedeEmitir}>
            {emision.isPending ? 'Emitiendo…' : 'Emitir'}
          </Boton>
        </div>
      </div>
    </div>
  )
}

function FilaTotal({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <dt className="text-cuerpo text-gris-600">{etiqueta}</dt>
      <dd>
        <Monto valor={valor} className="text-gris-900" />
      </dd>
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/* Tabla de captura                                                        */
/* ---------------------------------------------------------------------- */

/**
 * La rejilla de líneas se escribe aquí y no con `TablaDatos`: aquella resuelve
 * un listado del servidor —orden, filtros, paginación, virtualización— y esto
 * es un formulario tabular. Compartirlas obligaría a que la tabla de listado
 * supiera de campos editables, y es la clase de acoplamiento que después no se
 * deshace.
 */
function TablaDeLineas({
  formulario,
  filasRef,
}: {
  formulario: ReturnType<typeof useFormularioEmision>
  filasRef: React.RefObject<Map<string, HTMLInputElement>>
}) {
  const [nuevaLinea, setNuevaLinea] = useState<string | null>(null)

  // Al crear una línea con Enter, el foco salta a su descripción. Sin esto,
  // quien factura 80 veces al día tiene que buscar el campo con el ratón.
  useEffect(() => {
    if (nuevaLinea === null) return
    filasRef.current?.get(nuevaLinea)?.focus()
    setNuevaLinea(null)
  }, [nuevaLinea, filasRef])

  const agregar = useCallback(() => {
    setNuevaLinea(formulario.agregarLinea())
  }, [formulario])

  return (
    <section className="rounded-md border border-gris-300 bg-white" data-densidad="normal">
      <table className="w-full table-fixed border-collapse text-cuerpo">
        <thead className="bg-gris-50">
          <tr className="border-b border-gris-300">
            <th scope="col" className="h-[var(--alto-fila)] w-10 px-2 text-right text-etiqueta font-semibold text-gris-600">
              #
            </th>
            <th scope="col" className="h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600">
              Descripción
            </th>
            <th scope="col" className="h-[var(--alto-fila)] w-[96px] px-2 text-right text-etiqueta font-semibold text-gris-600">
              Cant
            </th>
            <th scope="col" className="h-[var(--alto-fila)] w-[130px] px-2 text-right text-etiqueta font-semibold text-gris-600">
              Precio
            </th>
            <th scope="col" className="h-[var(--alto-fila)] w-[110px] px-2 text-etiqueta font-semibold text-gris-600">
              Tipo
            </th>
            <th scope="col" className="h-[var(--alto-fila)] w-[150px] px-2 text-etiqueta font-semibold text-gris-600">
              ITBIS
            </th>
            <th scope="col" className="h-[var(--alto-fila)] w-[130px] px-2 text-right text-etiqueta font-semibold text-gris-600">
              Total
            </th>
            <th scope="col" className="h-[var(--alto-fila)] w-10 px-2">
              <span className="solo-lectores">Acciones</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {formulario.lineas.map((linea, indice) => (
            <FilaDeLinea
              key={linea.id}
              linea={linea}
              indice={indice}
              esUltima={indice === formulario.lineas.length - 1}
              total={formulario.totales.porLinea[indice]?.total.aTexto() ?? '0.00'}
              indicadorFijo={formulario.indicadorFijo}
              onCambiar={formulario.cambiarLinea}
              onBorrar={formulario.borrarLinea}
              onAgregar={agregar}
              registrar={(elemento) => {
                if (elemento === null) filasRef.current?.delete(linea.id)
                else filasRef.current?.set(linea.id, elemento)
              }}
              sePuedeBorrar={formulario.lineas.length > 1}
            />
          ))}
        </tbody>
      </table>

      <div className="border-t border-gris-300 px-2 py-2">
        <Boton variante="discreto" tamano="menor" icono={<Plus />} onClick={agregar} atajo="⏎">
          Agregar línea
        </Boton>
      </div>
    </section>
  )
}

function FilaDeLinea({
  linea,
  indice,
  esUltima,
  total,
  indicadorFijo,
  onCambiar,
  onBorrar,
  onAgregar,
  registrar,
  sePuedeBorrar,
}: {
  linea: LineaFormulario
  indice: number
  esUltima: boolean
  total: string
  indicadorFijo: IndicadorFacturacion | null
  onCambiar: (id: string, cambios: Partial<Omit<LineaFormulario, 'id'>>) => void
  onBorrar: (id: string) => void
  onAgregar: () => void
  registrar: (elemento: HTMLInputElement | null) => void
  sePuedeBorrar: boolean
}) {
  const [consulta, setConsulta] = useState(linea.descripcion)
  const [sugerenciasAbiertas, setSugerenciasAbiertas] = useState(false)
  const productos = useBuscarProductos(sugerenciasAbiertas ? consulta : '')

  const sugerencias = (productos.data?.filas ?? []).slice(0, 6)

  /** Enter en la última línea crea la siguiente. Es el atajo que más se usa. */
  const alTeclearEnUltimoCampo = (evento: React.KeyboardEvent): void => {
    if (evento.key === 'Enter' && !evento.ctrlKey && !evento.metaKey && esUltima) {
      evento.preventDefault()
      onAgregar()
    }
  }

  return (
    <tr className="h-[var(--alto-fila)] border-b border-gris-200">
      <td className="numero px-2 text-gris-600">{indice + 1}</td>

      <td className="relative overflow-visible px-1">
        <input
          ref={registrar}
          type="text"
          aria-label={`Descripción de la línea ${indice + 1}`}
          value={consulta}
          autoComplete="off"
          onChange={(evento) => {
            setConsulta(evento.target.value)
            setSugerenciasAbiertas(true)
            onCambiar(linea.id, { descripcion: evento.target.value })
          }}
          onFocus={() => setSugerenciasAbiertas(true)}
          onBlur={() => window.setTimeout(() => setSugerenciasAbiertas(false), 120)}
          className={cx(
            'h-[28px] w-full rounded-md border border-transparent bg-transparent px-1 text-cuerpo',
            'text-gris-900 hover:border-gris-300 focus:border-gris-500 focus:bg-white',
          )}
        />

        {sugerenciasAbiertas && consulta.trim() !== '' && sugerencias.length > 0 ? (
          <ul
            className={cx(
              'absolute left-1 top-full z-[var(--capa-panel)] mt-px w-[380px] overflow-hidden',
              'rounded-md border border-gris-300 bg-white shadow-flotante',
            )}
          >
            {sugerencias.map((producto) => (
              <li key={producto.id}>
                <button
                  type="button"
                  onMouseDown={(evento) => {
                    evento.preventDefault()
                    setConsulta(producto.descripcion)
                    setSugerenciasAbiertas(false)
                    // El catálogo guarda la **tasa**, no el indicador, y una
                    // tasa de cero es ambigua: puede ser tasa cero (indicador 3,
                    // conserva el derecho a deducir) o exento (indicador 4, no
                    // lo conserva). Cuando no se puede saber, la línea conserva
                    // el indicador que ya tenía y lo elige el usuario: acertar
                    // por sorteo se descubre en el 606, meses después.
                    const delCatalogo = indicadorDeTasa(producto.tasaItbis)
                    onCambiar(linea.id, {
                      descripcion: producto.descripcion,
                      precio: String(producto.precio),
                      codigoProducto: producto.codigo,
                      // El tipo manda sobre el producto: un ítem gravado en un
                      // 44 es rechazo garantizado.
                      ...(indicadorFijo !== null
                        ? { indicador: indicadorFijo }
                        : delCatalogo !== null
                          ? { indicador: delCatalogo }
                          : {}),
                    })
                  }}
                  className="flex h-[var(--alto-fila-normal)] w-full items-center justify-between gap-2 px-2 text-left hover:bg-marca-tenue"
                >
                  <span className="min-w-0 flex-1 truncate text-cuerpo text-gris-900">
                    {producto.descripcion}
                  </span>
                  <span className="mono shrink-0 text-gris-600">{producto.codigo}</span>
                  <Monto valor={producto.precio} className="shrink-0 w-[90px]" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </td>

      <td className="px-1">
        <input
          type="text"
          inputMode="decimal"
          aria-label={`Cantidad de la línea ${indice + 1}`}
          value={linea.cantidad}
          onChange={(evento) => onCambiar(linea.id, { cantidad: evento.target.value })}
          className="numero h-[28px] w-full rounded-md border border-transparent bg-transparent px-1 text-gris-900 hover:border-gris-300 focus:border-gris-500 focus:bg-white"
        />
      </td>

      <td className="px-1">
        <EntradaMonetaria
          aria-label={`Precio de la línea ${indice + 1}`}
          valor={linea.precio === '' ? null : Number(linea.precio)}
          onValorChange={(valor) => onCambiar(linea.id, { precio: valor === null ? '' : String(valor) })}
          mostrarSimbolo={false}
          decimales={4}
          className="h-[28px] border-transparent bg-transparent px-1 hover:border-gris-300 focus:border-gris-500 focus:bg-white"
        />
      </td>

      <td className="px-1">
        {/*
          Bien o servicio. Parece una etiqueta y no lo es: la retención de ISR del
          tipo 41 solo procede sobre servicios, así que marcarlo mal es facturar
          sin la retención que corresponde. Va antes del ITBIS porque es lo que el
          usuario decide primero al describir lo que vende.
        */}
        <select
          aria-label={`Bien o servicio de la línea ${indice + 1}`}
          value={linea.bienOServicio}
          onChange={(evento) =>
            onCambiar(linea.id, { bienOServicio: evento.target.value as '1' | '2' })
          }
          className="h-[28px] w-full rounded-md border border-transparent bg-transparent px-1 text-cuerpo text-gris-900 hover:border-gris-300 focus:border-gris-500 focus:bg-white"
        >
          <option value="1">Bien</option>
          <option value="2">Servicio</option>
        </select>
      </td>

      <td className="px-1">
        {indicadorFijo !== null ? (
          // El tipo fuerza el indicador: no se ofrece una opción que DGII va a
          // rechazar. Se enseña, deshabilitado, para que el usuario sepa por qué.
          <span className="flex h-[28px] items-center gap-2 px-1 text-etiqueta text-gris-600">
            <PuntoEstado tono="neutral" />
            {INDICADORES[indicadorFijo].etiqueta}
          </span>
        ) : (
          <select
            aria-label={`ITBIS de la línea ${indice + 1}`}
            value={linea.indicador}
            onChange={(evento) =>
              onCambiar(linea.id, { indicador: evento.target.value as IndicadorFacturacion })
            }
            className="h-[28px] w-full rounded-md border border-transparent bg-transparent px-1 text-cuerpo text-gris-900 hover:border-gris-300 focus:border-gris-500 focus:bg-white"
          >
            {(Object.keys(INDICADORES) as IndicadorFacturacion[]).map((indicador) => (
              <option key={indicador} value={indicador} title={INDICADORES[indicador].descripcion}>
                {INDICADORES[indicador].etiqueta}
              </option>
            ))}
          </select>
        )}
      </td>

      <td className="px-2" onKeyDown={alTeclearEnUltimoCampo}>
        <Monto valor={total} className="text-gris-900" />
      </td>

      <td className="px-1">
        <BotonIcono
          etiqueta={`Borrar la línea ${indice + 1}`}
          icono={<Trash2 />}
          tamano="menor"
          disabled={!sePuedeBorrar}
          onClick={() => onBorrar(linea.id)}
          onKeyDown={alTeclearEnUltimoCampo}
        />
      </td>
    </tr>
  )
}
